/**
 * Auth0 callback page — handles the redirect back from universal login,
 * lets the Auth0 SDK exchange the code for tokens, then redirects to the
 * intended destination (?returnTo from appState, or /dashboard).
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isAuth0Configured, useAuth0 } from "@/lib/auth0Provider";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();

  if (!isAuth0Configured()) {
    // SSO disabled — bounce straight to login.
    return <Redirector to="/login" />;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isLoading, isAuthenticated, error } = useAuth0();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get("returnTo") || "/dashboard";
      navigate(returnTo, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ek-canvas p-6">
        <div className="max-w-md rounded-xl bg-white p-8 ring-1 ring-rose-200" data-testid="sso-error">
          <p className="text-xs uppercase tracking-[0.2em] text-rose-700 font-semibold">SSO error</p>
          <p className="mt-2 text-sm text-rose-800">{error.message}</p>
          <button onClick={() => navigate("/login")} className="mt-4 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ek-canvas" data-testid="sso-callback-loading">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin text-violet-600" size={36} />
        <p className="text-sm text-ek-ink/70">Completing single sign-on…</p>
      </div>
    </div>
  );
}

function Redirector({ to }) {
  const navigate = useNavigate();
  useEffect(() => { navigate(to, { replace: true }); }, [navigate, to]);
  return null;
}
